/**
 * The `RateWire` a DTO carries for a `Rate` (ADR 060 — Money Type § 2.7), and
 * the two string ⇄ Decimal helpers `Rate.toWire` / `Rate.fromWire` are built on.
 */
import Decimal from 'decimal.js';

/**
 * A rate on the wire — its exact fraction as a decimal string at the six-place
 * storage scale (`"0.083750"` for 8.375%), byte-identical to what the
 * `numeric(12,6)` driver returns. A DTO declares `RateWire`, never a bare
 * `number` percent: a float on the wire is the drift ADR 060 exists to remove.
 *
 * Encode with `rate.toWire()`, decode with `Rate.fromWire(wire)`.
 */
export interface RateWire {
    /** Exact six-place fraction, e.g. `"0.083750"`. */
    fraction: string;
}

/** Fraction digits on the wire — the `numeric(12,6)` column's scale. */
const RATE_WIRE_SCALE = 6;

/** A plain decimal: optional minus, digits, optional fraction. No exponent, no spaces, no commas. */
const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/**
 * @param fraction - A normalized rate fraction.
 * @returns The wire form, zero-padded to the storage scale.
 */
export function encodeRateFraction(fraction: Decimal): RateWire {
    return { fraction: fraction.toFixed(RATE_WIRE_SCALE) };
}

/**
 * @param wire - A `RateWire` off a DTO.
 * @returns The exact fraction it carries.
 * @throws TypeError when `fraction` is not a plain decimal string.
 */
export function decodeRateFraction(wire: RateWire): Decimal {
    if (typeof wire.fraction !== 'string' || !DECIMAL_RE.test(wire.fraction)) {
        throw new TypeError(`RateWire fraction must be a plain decimal string, got ${JSON.stringify(wire.fraction)}`);
    }
    return new Decimal(wire.fraction);
}
